import {match} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  CheckoutProfileDocumentRowSchema,
  getCheckoutProfileDocumentCellKey,
  getCheckoutProfileDocumentCells,
  type CheckoutProfileError,
  type CheckoutProfileMissing,
  type CheckoutProfileWrite,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {
  getCheckoutProfileIssueMessage,
  type CheckoutProfileIssue,
} from '../../../utils/getCheckoutProfileIssueMessage';

type Options = {
  values: CheckoutProfileWrite;
  submittedValues?: CheckoutProfileWrite;
  missing?: CheckoutProfileMissing[];
  errors?: CheckoutProfileError[];
};

const documentIdentitySchema = CheckoutProfileDocumentRowSchema.pick({
  saleRegime: true,
  documentType: true,
  paymentMethod: true,
});

const getTab = (field: string): CheckoutProfileIssue['tab'] =>
  match(field.split('.')[0])
    .with('payments', () => 'PAYMENTS' as const)
    .with('deposit', () => 'DEPOSIT' as const)
    .with('documents', 'documentTypes', () => 'DOCUMENTS' as const)
    .with(
      'name',
      'branchId',
      'typeOfSale',
      'isActive',
      'saleRegimes',
      'foreignCurrency',
      () => 'GENERAL' as const
    )
    .otherwise(() => undefined);

const getMissingDetails = (code: string, values: CheckoutProfileWrite) =>
  match(code)
    .with('PAYMENT_METHOD_NONE', () => ({
      field: 'payments.methods',
      message: i18n.t('entity.checkout.validation.paymentMethodNone'),
    }))
    .with('DEPOSIT_METHOD_NONE', () => ({
      field: 'deposit.methods',
      message: i18n.t('entity.checkout.validation.depositMethodNone'),
    }))
    .with('DEFAULT_PAYMENT_METHOD_MISSING', () => ({
      field: 'payments.defaultMethod',
      message: i18n.t('entity.checkout.validation.defaultPaymentMethodMissing'),
    }))
    .with('CASH_REGISTER_MISSING', () => {
      const index =
        values.payments?.methods.findIndex((payment) => payment.method === 'CASH') ?? -1;

      return {
        field: index >= 0 ? `payments.methods.${index}.cashRegisterId` : undefined,
        message: i18n.t('entity.checkout.validation.cashRegisterMissing'),
      };
    })
    .with('DEFAULT_DEPOSIT_METHOD_MISSING', () => ({
      field: 'deposit.defaultMethod',
      message: i18n.t('entity.checkout.validation.defaultDepositMethodMissing'),
    }))
    .with('DEPOSIT_MATURITY_MISSING', () => ({
      field: 'deposit.maturityDays',
      message: i18n.t('entity.checkout.validation.depositMaturityMissing'),
    }))
    .with('FOREIGN_CURRENCY_MISSING', () => ({
      field: 'foreignCurrency.defaultCurrency',
      message: i18n.t('entity.checkout.validation.foreignCurrencyMissing'),
    }))
    .with('DOCUMENT_ROW_MISSING', () => ({
      field: undefined,
      message: i18n.t('entity.checkout.validation.documentSettingsMissing'),
    }))
    .otherwise(() => ({
      field: undefined,
      message: i18n.t('entity.checkout.validation.configurationIncomplete', {code}),
    }));

export function getCheckoutProfileServerIssues(options: Options): CheckoutProfileIssue[] {
  const cells = getCheckoutProfileDocumentCells(options.values);
  const isVisible = (issue: Pick<CheckoutProfileIssue, 'tab' | 'saleRegime'>) =>
    !(issue.tab === 'PAYMENTS' && options.values.typeOfSale === 'INTERNAL') &&
    !(
      issue.tab === 'DEPOSIT' &&
      (options.values.typeOfSale === 'INTERNAL' || !options.values.deposit?.isAllowed)
    ) &&
    (issue.saleRegime !== 'IN_EUROPE' || !!options.values.saleRegimes?.inEurope) &&
    (issue.saleRegime !== 'OUT_OF_EUROPE' || !!options.values.saleRegimes?.outOfEurope);

  const missing = [
    ...(options.missing ?? []),
    ...(options.errors ?? []).flatMap((error) => error.missing ?? []),
  ]
    .filter(
      (item) =>
        isVisible(item) &&
        (isNil(item.documentType) ||
          cells.some(
            (cell) =>
              cell.documentType === item.documentType &&
              cell.saleRegime === (item.saleRegime ?? null) &&
              cell.paymentMethod === (item.paymentMethod ?? null)
          ))
    )
    .map((item): CheckoutProfileIssue => ({
      ...item,
      ...getMissingDetails(item.code, options.values),
      severity: 'warning',
    }));

  const errors = (options.errors ?? [])
    .map((error): CheckoutProfileIssue => {
      const field = error.path.replace(/\[(\d+)\]/g, '.$1');
      const parts = field.split('.');

      if (
        parts[0] === 'documents' &&
        ['allowedSeriesIds', 'allowedTemplateIds'].includes(parts[2])
      ) {
        parts.splice(3);
      }

      if (parts[0] === 'payments' && parts[3] === 'bankAccount') {
        parts.splice(4);
      }

      const identity = documentIdentitySchema.safeParse(error);
      const submittedDocuments = (options.submittedValues ?? options.values).documents;
      const submittedDocument = identity.success
        ? submittedDocuments.find(
            (row) =>
              getCheckoutProfileDocumentCellKey(row) ===
              getCheckoutProfileDocumentCellKey(identity.data)
          )
        : submittedDocuments[Number(parts[1])];

      const document = parts[0] === 'documents' ? submittedDocument : undefined;
      const cell = parts[0] === 'documents' && identity.success ? identity.data : document;
      const index = cell
        ? options.values.documents.findIndex(
            (row) =>
              getCheckoutProfileDocumentCellKey(row) === getCheckoutProfileDocumentCellKey(cell)
          )
        : -1;

      const defaultValue =
        document && parts[2] === 'defaultSeriesId'
          ? document.defaultSeriesId
          : document?.defaultTemplateId;

      const allowed =
        parts[2] === 'defaultSeriesId' ? document?.allowedSeriesIds : document?.allowedTemplateIds;

      const optionalDefaultRefused =
        error.code === 'DEFAULT_NOT_IN_ALLOWED' &&
        document &&
        ['defaultSeriesId', 'defaultTemplateId'].includes(parts[2]) &&
        (isNilOrEmpty(defaultValue) || isNilOrEmpty(allowed));

      let mappedField = parts.join('.') || undefined;

      if (cell) {
        mappedField =
          index >= 0 && parts.length > 2
            ? ['documents', index, ...parts.slice(2)].join('.')
            : undefined;
      }

      if (optionalDefaultRefused || error.code === 'PROFILE_INCOMPLETE') {
        mappedField = undefined;
      }

      return {
        code: error.code,
        severity: 'error',
        message: optionalDefaultRefused
          ? i18n.t('entity.checkout.validation.optionalDocumentDefaultsRefused')
          : getCheckoutProfileIssueMessage(error.code, error.message),
        tab: getTab(field),
        field: mappedField,
        ...(cell && {
          saleRegime: cell.saleRegime,
          documentType: cell.documentType,
          paymentMethod: cell.paymentMethod,
        }),
      };
    })
    .filter(isVisible);

  return [
    ...new Map(
      [...missing, ...errors].map((issue) => [
        [
          issue.severity,
          issue.code,
          issue.tab,
          issue.saleRegime,
          issue.documentType,
          issue.paymentMethod,
          issue.field,
        ].join('|'),
        issue,
      ])
    ).values(),
  ];
}
